import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useConfig } from '../context/ConfigContext';
import { downloadListCsv } from '../utils/csv';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/ProcurementOrders.jsx (Purchase
// Orders list). `status` is not list-item-backed — fixed states set by
// ProcurementFulfillmentController/PurchaseOrder lifecycle.
const STATUS_LABELS: Record<string, string> = {
  issued: 'Issued',
  sent_to_vendor: 'Sent to Vendor',
  partially_fulfilled: 'Partially Fulfilled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function statusBadgeClass(status: string): string {
  if (status === 'completed') return 'badge-normal';
  if (status === 'cancelled') return 'badge-important';
  return 'badge-neutral';
}

interface PurchaseOrderRow {
  id: number;
  po_number: string;
  vendor_name: string;
  amount: number;
  status: string;
  purchase_request: { reference: string; item_name: string } | null;
}

export function ProcurementOrders() {
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const filters = useMemo(
    () => ({ status: status || undefined, search: search || undefined }),
    [status, search]
  );
  const { data, loading, error, setPage } = usePaginatedList<PurchaseOrderRow>('/purchase-orders', filters);

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<PurchaseOrderRow>(
      api,
      '/purchase-orders',
      { status: status || undefined, search: search || undefined },
      [
        { label: 'Order No', value: (r) => r.po_number },
        { label: 'Vendor', value: (r) => r.vendor_name },
        { label: 'Request Reference', value: (r) => r.purchase_request?.reference ?? '' },
        { label: 'Item', value: (r) => r.purchase_request?.item_name ?? '' },
        { label: 'Amount', value: (r) => r.amount },
        { label: 'Status', value: (r) => STATUS_LABELS[r.status] ?? r.status },
      ],
      'purchase-orders.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Orders</h1>
        <Link className="btn btn-primary" to="/procurement/orders/new">
          + Add Orders
        </Link>
      </header>
      <p className="muted">Purchase orders issued to vendors against approved purchase requests.</p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search PO number or vendor…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Order No</th>
                <th>Vendor</th>
                <th>Request</th>
                <th className="right">Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((po) => (
                  <tr key={po.id}>
                    <td className="muted">{po.po_number}</td>
                    <td>{po.vendor_name}</td>
                    <td>
                      <strong>{po.purchase_request?.item_name}</strong>
                      <div>
                        <small className="muted">{po.purchase_request?.reference}</small>
                      </div>
                    </td>
                    <td className="right">{fmtMoney(po.amount)}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(po.status)}`}>
                        {STATUS_LABELS[po.status] ?? po.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/procurement/orders/${po.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No purchase orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
